import React, { useState, useEffect, useMemo } from "react";
import empty from "is-empty";
import { useSelector, useDispatch } from "react-redux";

import { SettingsSelectors } from "src/redux/Settings/selectors";
import { AuthSelectors } from "src/redux/Auth/selectors";
import { SettingsActions } from "src/redux/Settings/actions";

import styles from "./styles.module.scss";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-enterprise";
import {
    listDefaultColDef,
    getDetailListColumnDefs,
    getCreateListColumnDefs,
} from "./column_defs";
import Button from "src/components/common/Button";
import { store } from "src/index";
import * as Validators from "src/utils/validators";
import { INTERMEDIARY_ERRORS } from "src/redux/Settings/constants";
import { ModalActions } from "src/redux/Modal/actions";
import SettingsIcon from "src/asset/icon-settings.svg";
import AvailabilitySettings from "../AvailabilitySettings";

const mapServerAvailabilitytoUi = (server) => ({
    showRate: server.advertiseRate,
    cptyIds: server.availabilityCptyIds,
    enabled: server.enabled,
});

const mapUiAvailabilitytoServer = (ui) => ({
    advertiseRate: ui.showRate,
    availabilityCptyIds: ui.cptyIds,
    enabled: ui.enabled,
});

class DeleteButton {
    init(params) {
        this.eGui = document.createElement("div");
        this.eGui.className = styles.deleteBtn;
        this.element = document.createElement("div");
        this.element.className = `${styles.delete} delete-btn`;
        // setAttribute keeps the (user-authored) list name inert - never parsed as HTML.
        this.element.setAttribute("data-testid", `delete-${params.data?.name}`);
        this.element.setAttribute("aria-label", `Delete ${params.data?.name}`);
        this.element.onclick = () => this.handleDelete(params);
        this.eGui.appendChild(this.element);
    }

    handleDelete(params) {
        store.dispatch(SettingsActions.RemoveListSettings(params.data));
    }

    getGui() {
        return this.eGui;
    }
}

export default () => {
    const dispatch = useDispatch();
    const isTraderRole = useSelector(AuthSelectors.IsTraderRole);

    const [newSetting, setNewSetting] = useState({});
    const [newListGridApi, setNewListGridApi] = useState(null);
    const [listGridApi, setListGridApi] = useState(null);
    const [errors, setErrors] = useState(null);
    const [refreshTable, setRefreshTable] = useState(null);
    const showErrors = useSelector(SettingsSelectors.DisplayListErrors);
    // Confirmed lists plus this dialog's drafts: Add shows the new list
    // immediately, and the drafts never reach the collection the offer grid
    // reads. See docs/plans/list-settings-pending-drafts.md.
    const listSettings = useSelector(SettingsSelectors.ListSettingsForDialog);
    const dynamicAndClientPoolList = useSelector(SettingsSelectors.DynamicAndClientPoolList);
    const {
        "enable-multi-account-code-support": isAccountSelectable,
        "enable-list-level-availability": isListLevelAvailability,
    } = useSelector(SettingsSelectors.FeatureFlags);

    const accounts = useSelector(SettingsSelectors.GeneralAccountSettings);

    const createListColumnDefs = useMemo(
        () => getCreateListColumnDefs({ isAccountSelectable }),
        [isAccountSelectable]
    );

    const detailListColumnDefs = useMemo(
        () =>
            getDetailListColumnDefs({
                isAccountSelectable,
                isListLevelAvailability,
            }),
        [isAccountSelectable, isListLevelAvailability]
    );

    useEffect(() => {
        return () => dispatch(SettingsActions.SetDisplayNewListErrors(false));
    }, [dispatch]);

    const addListSetting = () => {
        if (newListGridApi) newListGridApi.stopEditing();
        if (!empty(newSetting)) {
            dispatch(SettingsActions.AddListSettings(newSetting))
                .then(() => {
                    setNewSetting({});
                    setRefreshTable(true);
                })
                .catch((e) => {
                    newListGridApi.redrawRows();
                });
        }
    };

    // Every exit unmounts this dialog (Save and Cancel both dispatch
    // ModalActions.Close, and the backdrop and Escape close it too), so the
    // cleanup is the one place that reliably wipes drafts. Without it a draft
    // reappears the next time the dialog opens.
    useEffect(
        () => () => {
            dispatch(SettingsActions.ClearPendingListSettings());
        },
        [dispatch]
    );

    const handleRowDataChanged = (e) => {
        if (!refreshTable || !listGridApi || empty(listSettings)) return;

        const index = listSettings.length - 1;
        listGridApi.ensureIndexVisible(index, "bottom");
        setRefreshTable(false);
    };

    const handleNewListGridReady = (params) => {
        setNewListGridApi(params.api);
    };
    const handleListGridReady = (params) => {
        setListGridApi(params.api);
    };

    const handleCellValueChange = (params) => {
        setErrors(
            Validators.getListValidationErrors(
                params.data,
                listSettings,
                dynamicAndClientPoolList,
                isAccountSelectable
            )
        );
    };

    return (
        <div className={styles.listSettings}>
            <div className={styles.addSetting} data-testid="new-list-setting">
                <div
                    className={[styles.table, "ag-theme-alpine-dark"].join(" ")}
                >
                    <AgGridReact
                        rowBuffer={50}
                        rowHeight={40}
                        headerHeight={40}
                        onGridReady={handleNewListGridReady}
                        rowData={[newSetting]}
                        defaultColDef={listDefaultColDef}
                        columnDefs={createListColumnDefs}
                        onCellValueChanged={handleCellValueChange}
                        singleClickEdit
                        suppressContextMenu={true}
                        context={{ accounts }}
                    />
                </div>
            </div>
            <div className={styles.bottom}>
                {showErrors && !empty(errors) ? (
                    <span
                        className={styles.errorMessage}
                        data-testid={"list-settings-error-message"}
                    >
                        {INTERMEDIARY_ERRORS[errors[0]]}
                    </span>
                ) : (
                    !isAccountSelectable && (
                        <div className={styles.message}>
                            <div className={styles.rect} />
                            Either SLB Num or Fund Code must be entered
                        </div>
                    )
                )}
                <Button
                    onClick={addListSetting}
                    disabled={
                        empty(newSetting) || (showErrors && !empty(errors))
                    }
                    color="reversed"
                    dataTestId="add-list-settings"
                    value="Add"
                />
            </div>
            <div className={styles.list} data-testid="list-settings">
                <div
                    className={[styles.table, "ag-theme-alpine-dark"].join(" ")}
                >
                    <AgGridReact
                        rowBuffer={50}
                        rowHeight={40}
                        headerHeight={40}
                        rowData={listSettings}
                        onGridReady={handleListGridReady}
                        getRowId={(params) => params.data.name}
                        defaultColDef={listDefaultColDef}
                        columnDefs={detailListColumnDefs}
                        components={{
                            deleteButton: DeleteButton,
                            availabilityButton: AvailabilitySettingsButton,
                        }}
                        enableCellChangeFlash={true}
                        onRowDataChanged={handleRowDataChanged}
                        suppressScrollOnNewData={true}
                        suppressContextMenu={true}
                        scrollbarWidth={8}
                        suppressHorizontalScroll={true}
                    />
                </div>
            </div>
            <div className={`${styles.bottom} ${styles.buttons}`}>
                <Button
                    onClick={() =>
                        dispatch(SettingsActions.updateLenderListSettings())
                    }
                    dataTestId="save-list-settings"
                    value="Save"
                    disabled={!isTraderRole}
                />
                <Button
                    onClick={() => dispatch(ModalActions.Close())}
                    color="reversedWhite"
                    dataTestId="cancel-list-settings"
                    value="Cancel"
                />
            </div>
        </div>
    );
};

const AvailabilitySettingsButton = ({ value, data }) => {
    // kludge fix for when advertise availability settings are not set (e.g. old lists)
    if (value === undefined) {
        value = {
            enabled: false,
            advertiseRate: false,
            availabilityCptyIds: [],
        };
    }

    const dispatch = useDispatch();
    return (
        <div className={styles.buttonWrapper}>
            <button
                data-testid={`${data.name}-availability-settings-button`}
                className={styles.availabilitySettingsButton}
                onClick={() =>
                    dispatch(
                        ModalActions.Open(AvailabilitySettings, {
                            width: 500,
                            height: 675,
                            ignoreClickOutside: true,
                            title: "Outbox Settings",
                            settings: true,
                            customProps: {
                                availabilitySettings:
                                    mapServerAvailabilitytoUi(value),
                                onSave: (settings) => {
                                    dispatch(
                                        SettingsActions.EditAvailability(
                                            data.name,
                                            mapUiAvailabilitytoServer(settings)
                                        )
                                    );
                                    dispatch(ModalActions.Close());
                                },
                                isListLevel: true,
                                listName: data.name,
                            },
                        })
                    )
                }
            />
        </div>
    );
};
